import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Pause, Play, Trash2, Download, Copy, RefreshCw, Radio } from "lucide-react";
import { toast } from "sonner";

const METHOD_COLORS = {
  GET: "bg-sage-light text-sage",
  POST: "bg-terracotta-light text-terracotta",
  PUT: "bg-info/10 text-info",
  PATCH: "bg-info/10 text-info",
  DELETE: "bg-error/10 text-error",
};

export default function AdminLogs() {
  const [paused, setPaused] = useState(false);
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [qFilter, setQFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [liveLogs, setLiveLogs] = useState([]); // appended from WebSocket
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting|live|fallback
  const wsRef = useRef(null);

  // Initial load via REST (gets 200 most recent rows). Subsequent updates come
  // from the WebSocket so polling can be disabled. If the WS fails to open
  // we fall back to a 3s polling interval.
  const { data: serverLogs, refetch } = useQuery({
    queryKey: ["request-logs", methodFilter, statusFilter, qFilter],
    queryFn: async () => {
      const params = { limit: 200 };
      if (methodFilter !== "all") params.method = methodFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (qFilter) params.q = qFilter;
      return (await api.get("/admin/request-logs", { params })).data.data;
    },
    refetchInterval: paused ? false : (wsStatus === "live" ? false : 3000),
  });

  // WebSocket subscription — pushes each new log into liveLogs
  useEffect(() => {
    if (paused) {
      wsRef.current?.close();
      return undefined;
    }
    const token = localStorage.getItem("sirh_token");
    if (!token) return undefined;
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/admin/logs?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setWsStatus("live");
    ws.onerror = () => setWsStatus("fallback");
    ws.onclose = () => setWsStatus((s) => (s === "live" ? "fallback" : s));
    ws.onmessage = (ev) => {
      try {
        const log = JSON.parse(ev.data);
        setLiveLogs((prev) => [log, ...prev].slice(0, 200));
      } catch (e) { console.warn("WS parse:", e); }
    };
    return () => ws.close();
  }, [paused]);

  // Combine server-loaded logs with live-streamed ones, dedupe by id, keep newest first
  const logs = React.useMemo(() => {
    const seen = new Set();
    const merged = [];
    const passes = (r) => {
      if (methodFilter !== "all" && r.method !== methodFilter) return false;
      if (statusFilter === "2xx" && !(r.status >= 200 && r.status < 300)) return false;
      if (statusFilter === "4xx" && !(r.status >= 400 && r.status < 500)) return false;
      if (statusFilter === "5xx" && !(r.status >= 500 && r.status < 600)) return false;
      if (qFilter && !(r.endpoint || "").toLowerCase().includes(qFilter.toLowerCase()) &&
          !((r.request_body || "").toLowerCase().includes(qFilter.toLowerCase()))) return false;
      return true;
    };
    for (const arr of [liveLogs, serverLogs || []]) {
      for (const r of arr) {
        if (!r || seen.has(r.id)) continue;
        seen.add(r.id);
        if (passes(r)) merged.push(r);
      }
    }
    return merged.slice(0, 200);
  }, [liveLogs, serverLogs, methodFilter, statusFilter, qFilter]);

  const { data: exportsHistory } = useQuery({
    queryKey: ["export-history"],
    queryFn: async () => (await api.get("/admin/export-history", { params: { limit: 200 } })).data.data,
  });

  const { data: webhookDeliveries } = useQuery({
    queryKey: ["webhook-deliveries"],
    queryFn: async () => (await api.get("/admin/webhook-deliveries", { params: { limit: 200 } })).data.data,
    refetchInterval: paused ? false : 5000,
  });

  const clearLogs = async () => {
    if (!window.confirm("Effacer l'historique complet ?")) return;
    await api.delete("/admin/request-logs");
    toast.success("Logs effacés");
    refetch();
  };

  const downloadLogs = (format) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/request-logs/export?format=${format}&token=${localStorage.getItem("sirh_token")}`, "_blank");
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="admin-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-terracotta-light text-terracotta text-[12px] font-medium mb-3">
          Inspecteur NIM
        </div>
        <h1 className="font-heading text-[40px] font-semibold text-warm-dark tracking-tight">Admin & Logs</h1>
        <p className="text-[15px] text-warm-dark/70 mt-1">Trafic API entrant, exports CSV, et livraisons webhook en temps réel.</p>
      </div>

      <Tabs defaultValue="api">
        <TabsList>
          <TabsTrigger value="api" data-testid="tab-api">Appels API entrants</TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports">Exports CSV</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks-log">Webhooks émis</TabsTrigger>
        </TabsList>

        <TabsContent value="api" className="mt-4">
          <Card className="border-sand shadow-soft rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-sand bg-off-white/50 flex items-center gap-3 flex-wrap">
              <Input placeholder="Filtrer endpoint / body…" value={qFilter} onChange={(e) => setQFilter(e.target.value)} className="h-9 max-w-xs bg-off-white border-sand" data-testid="admin-filter-q" />
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="h-9 w-36" data-testid="admin-filter-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes méthodes</SelectItem>
                  {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-36" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="2xx">2xx succès</SelectItem>
                  <SelectItem value="4xx">4xx client</SelectItem>
                  <SelectItem value="5xx">5xx serveur</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => setPaused(!paused)} className="ml-auto" data-testid="admin-pause">
                {paused ? <><Play size={14} strokeWidth={1.5} className="mr-1.5" />Reprendre</> : <><Pause size={14} strokeWidth={1.5} className="mr-1.5" />Pause</>}
              </Button>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                  paused ? "bg-sand text-warm-dark/60" :
                  wsStatus === "live" ? "bg-sage-light text-sage" :
                  wsStatus === "fallback" ? "bg-warning/20 text-warning" :
                  "bg-cream text-warm-dark/50"
                }`}
                data-testid="admin-ws-status"
                title={wsStatus === "live" ? "Diffusion temps réel via WebSocket" : wsStatus === "fallback" ? "WebSocket fermé — repli sur polling 3s" : "Connexion en cours…"}
              >
                <Radio size={11} strokeWidth={2} className={wsStatus === "live" && !paused ? "animate-pulse" : ""} />
                {paused ? "Pause" : wsStatus === "live" ? "Live" : wsStatus === "fallback" ? "Polling 3s" : "…"}
              </span>
              <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="admin-refresh"><RefreshCw size={14} strokeWidth={1.5} /></Button>
              <Select onValueChange={downloadLogs}>
                <SelectTrigger className="h-9 w-44" data-testid="admin-export"><SelectValue placeholder="Exporter…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="har">Format HAR</SelectItem>
                  <SelectItem value="json">Format JSON</SelectItem>
                  <SelectItem value="curl">Format cURL</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={clearLogs} className="text-error border-error/30" data-testid="admin-clear"><Trash2 size={14} strokeWidth={1.5} /></Button>
            </div>

            <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-cream sticky top-0">
                  <tr className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60">
                    <th className="px-4 py-2.5 text-left">Horodatage</th>
                    <th className="px-2 py-2.5">Méthode</th>
                    <th className="px-2 py-2.5 text-left">Endpoint</th>
                    <th className="px-2 py-2.5">Statut</th>
                    <th className="px-2 py-2.5 text-right">Durée</th>
                    <th className="px-4 py-2.5 text-left">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand">
                  {(logs || []).map((r) => (
                    <tr key={r.id} onClick={() => setSelected(r)} className="hover:bg-terracotta-light/30 cursor-pointer" data-testid={`log-row-${r.id}`}>
                      <td className="px-4 py-2 font-mono text-[11px] text-warm-dark/60 whitespace-nowrap">{formatTime(r.timestamp)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${METHOD_COLORS[r.method] || "bg-muted"}`}>{r.method}</span>
                      </td>
                      <td className="px-2 py-2 font-mono text-[12px] text-warm-dark truncate max-w-md">{r.endpoint}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${statusColor(r.status)}`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-[11px] text-warm-dark/70">{r.duration_ms}ms</td>
                      <td className="px-4 py-2 text-[11px] text-warm-dark/60 truncate">{r.source_ip || "—"}</td>
                    </tr>
                  ))}
                  {(logs || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center py-10 text-warm-dark/50 text-[13px]">Aucun appel API enregistré — générez un peu de trafic et revenez ici.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          <LogDrawer log={selected} onClose={() => setSelected(null)} />
        </TabsContent>

        <TabsContent value="exports" className="mt-4">
          <Card className="border-sand shadow-soft rounded-xl overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-cream">
                <tr className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60">
                  <th className="px-4 py-2.5 text-left">Horodatage</th>
                  <th className="px-2 py-2.5">Type</th>
                  <th className="px-2 py-2.5 text-left">Entité</th>
                  <th className="px-2 py-2.5 text-left">Fichier</th>
                  <th className="px-2 py-2.5 text-right">Taille</th>
                  <th className="px-2 py-2.5">Statut</th>
                  <th className="px-2 py-2.5 text-right">Durée</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand">
                {(exportsHistory || []).map((e) => (
                  <tr key={e.id} data-testid={`export-row-${e.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-warm-dark/60">{formatTime(e.timestamp)}</td>
                    <td className="px-2 py-2 text-center text-[11px] text-warm-dark/70">{e.type}</td>
                    <td className="px-2 py-2 text-warm-dark">{e.entity}</td>
                    <td className="px-2 py-2 font-mono text-[12px] text-warm-dark/80">{e.file}</td>
                    <td className="px-2 py-2 text-right font-mono text-[11px] text-warm-dark/70">{(e.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${e.status === "success" ? "bg-sage-light text-sage" : "bg-error/10 text-error"}`}>{e.status}</span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[11px] text-warm-dark/70">{e.duration_ms}ms</td>
                  </tr>
                ))}
                {(exportsHistory || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-warm-dark/50 text-[13px]">Aucun export enregistré.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="mt-4">
          <Card className="border-sand shadow-soft rounded-xl overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-cream">
                <tr className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60">
                  <th className="px-4 py-2.5 text-left">Horodatage</th>
                  <th className="px-2 py-2.5 text-left">Événement</th>
                  <th className="px-2 py-2.5 text-left">URL cible</th>
                  <th className="px-2 py-2.5">Statut HTTP</th>
                  <th className="px-2 py-2.5 text-right">Tentatives</th>
                  <th className="px-2 py-2.5 text-right">Durée</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand">
                {(webhookDeliveries || []).map((d) => (
                  <tr key={d.id} data-testid={`delivery-row-${d.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-warm-dark/60">{formatTime(d.timestamp)}</td>
                    <td className="px-2 py-2 font-mono text-[12px] text-terracotta">{d.event}</td>
                    <td className="px-2 py-2 font-mono text-[12px] text-warm-dark/80 truncate max-w-sm">{d.target_url}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${d.status_code >= 200 && d.status_code < 300 ? "bg-sage-light text-sage" : "bg-error/10 text-error"}`}>{d.status_code || "—"}</span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[11px] text-warm-dark/70">{d.retry_count}</td>
                    <td className="px-2 py-2 text-right font-mono text-[11px] text-warm-dark/70">{d.duration_ms}ms</td>
                  </tr>
                ))}
                {(webhookDeliveries || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-warm-dark/50 text-[13px]">Aucune livraison enregistrée — configurez un webhook.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LogDrawer({ log, onClose }) {
  const copyAsCurl = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    let c = `curl -X ${log.method} '${base}${log.endpoint}'`;
    if (log.request_body) c += ` \\\n  -H 'Content-Type: application/json' -d '${log.request_body}'`;
    navigator.clipboard.writeText(c);
    toast.success("Copié en cURL");
  };
  const replay = async () => {
    try {
      await api.request({
        method: log.method,
        url: log.endpoint.replace(/^\/api/, ""),
        data: log.request_body ? JSON.parse(log.request_body) : undefined,
      });
      toast.success("Requête rejouée");
    } catch {
      toast.error("Échec du replay");
    }
  };
  return (
    <Sheet open={!!log} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[620px] sm:max-w-[620px] overflow-y-auto bg-off-white">
        {log && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading">{log.method} {log.endpoint}</SheetTitle>
            </SheetHeader>
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={copyAsCurl} data-testid="log-copy-curl"><Copy size={13} strokeWidth={1.5} className="mr-1.5" />Copier en cURL</Button>
              <Button size="sm" variant="outline" onClick={replay} data-testid="log-replay"><RefreshCw size={13} strokeWidth={1.5} className="mr-1.5" />Rejouer</Button>
            </div>
            <div className="mt-5 space-y-4">
              <Section title="Métadonnées">
                <DL label="Horodatage" value={formatTime(log.timestamp)} />
                <DL label="Statut" value={log.status} />
                <DL label="Durée" value={`${log.duration_ms} ms`} />
                <DL label="IP source" value={log.source_ip || "—"} />
                <DL label="User-Agent" value={log.user_agent} mono small />
              </Section>
              <Section title="Requête"><Pre>{log.request_body || "(pas de body)"}</Pre></Section>
              <Section title="Réponse"><Pre>{log.response_body || "(pas de body)"}</Pre></Section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60 mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function DL({ label, value, mono, small }) {
  return (
    <div className="flex items-baseline gap-3 text-[13px]">
      <span className="w-28 text-warm-dark/60 text-[12px]">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${small ? "text-[11px]" : ""} text-warm-dark break-all`}>{value}</span>
    </div>
  );
}
function Pre({ children }) {
  return <pre className="bg-cream border border-sand rounded-lg p-3 text-[12px] font-mono text-warm-dark overflow-x-auto max-h-60">{children}</pre>;
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-FR") + " " + new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}
function statusColor(s) {
  if (!s) return "bg-muted text-warm-dark/60";
  if (s >= 200 && s < 300) return "bg-sage-light text-sage";
  if (s >= 300 && s < 400) return "bg-info/10 text-info";
  if (s >= 400 && s < 500) return "bg-warning/20 text-warning";
  return "bg-error/10 text-error";
}
